import { useQuery } from '@apollo/client';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { useStore } from 'context/store-context';
import { useUser } from 'context/user-context';
import { RETURN_INSPECTION_QUEUE } from 'gql/returnInspection';
import { canManageRequests } from 'types/resolution';
import {
  ReturnInspectionQueueResult,
  ReturnInspectionQueueStatus,
} from 'types/returnInspection';
import { isShipaidAdmin } from 'utils';
import { withRequestReturnTo } from 'utils/requestNavigation';
import { returnInspectionReasonLabel } from 'utils/returnInspectionReason';

const date = (value?: string | null) =>
  value && Number.isFinite(Date.parse(value))
    ? new Date(value).toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    : '—';
const safeTrackingUrl = (value?: string | null) => {
  try {
    const url = new URL(value || '');
    return url.protocol === 'https:' && !url.username && !url.password
      ? url.href
      : undefined;
  } catch {
    return undefined;
  }
};
const buttonClass =
  'rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50';
export function ReturnInspectionQueue() {
  const { storeId } = useStore();
  const { role, user } = useUser();
  const allowed = canManageRequests(role, isShipaidAdmin(user));
  const [params, setParams] = useSearchParams();
  const status: ReturnInspectionQueueStatus = [
    'PENDING',
    'AWAITING_WAREHOUSE',
    'CLOSED',
  ].includes(params.get('status') || '')
    ? (params.get('status') as ReturnInspectionQueueStatus)
    : 'PENDING';
  const page = /^\d+$/.test(params.get('page') || '')
    ? Math.min(401, Math.max(1, Number(params.get('page'))))
    : 1;
  const currentFilters = { storeId, status, offset: (page - 1) * 25 };
  const setFilters = (value: typeof currentFilters) => {
    const next = new URLSearchParams();
    if (value.status !== 'PENDING') next.set('status', value.status);
    if (value.offset) next.set('page', String(value.offset / 25 + 1));
    setParams(next);
  };
  const { pathname, search } = useLocation();
  const { data, loading, error, refetch } =
    useQuery<ReturnInspectionQueueResult>(RETURN_INSPECTION_QUEUE, {
      variables: {
        input: {
          storeId,
          status: currentFilters.status,
          limit: 25,
          offset: currentFilters.offset,
        },
      },
      skip: !storeId || !allowed,
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
    });
  const current =
    data?.returnInspectionQueue?.storeId === storeId
      ? data?.returnInspectionQueue
      : undefined;
  if (!allowed)
    return (
      <p className="p-6">
        An Admin or Manager role is required to view inspections.
      </p>
    );
  if (!storeId)
    return <p className="p-6">Choose a store to view inspections.</p>;
  return (
    <div className="mx-auto max-w-7xl space-y-4 px-4 sm:px-6 md:px-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">
            Return inspections
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Review delivered returns before settlement.
          </p>
        </div>
        <label className="text-xs font-medium text-gray-600">
          Inspection queue
          <select
            className="ml-3 rounded-lg border border-gray-300 px-3 py-2 text-sm"
            value={currentFilters.status}
            onChange={(event) =>
              setFilters({
                storeId,
                status: event.target.value as ReturnInspectionQueueStatus,
                offset: 0,
              })
            }
          >
            <option value="PENDING">Needs inspection</option>
            <option value="AWAITING_WAREHOUSE">Awaiting warehouse</option>
            <option value="CLOSED">Reviewed</option>
          </select>
        </label>
      </div>
      {error ? (
        <div
          role="alert"
          className="rounded-xl border border-red-200 bg-red-50 p-5 text-sm text-red-800"
        >
          <p>Inspection tasks could not be loaded.</p>
          <button
            type="button"
            className={`${buttonClass} mt-3`}
            disabled={loading}
            onClick={() => refetch()}
          >
            {loading ? 'Retrying inspections…' : 'Retry inspections'}
          </button>
        </div>
      ) : loading || !current ? (
        <p
          role="status"
          className="rounded-xl border border-gray-200 bg-white p-6 text-sm text-gray-500"
        >
          Loading inspection tasks…
        </p>
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table
              aria-label="Inspection tasks"
              className="w-full min-w-[1000px] text-left text-sm"
            >
              <thead className="border-b border-gray-200 bg-gray-50 text-xs uppercase text-gray-500">
                <tr>
                  {[
                    'Return / customer',
                    'Returned items',
                    'Review reason',
                    'Delivery',
                    'Next step',
                  ].map((label) => (
                    <th key={label} className="px-4 py-3">
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {current.nodes.map((task) => (
                  <tr key={task.id} className="align-top">
                    <td className="px-4 py-4">
                      <Link
                        className="font-semibold text-blue-700 hover:underline"
                        to={withRequestReturnTo(
                          `/returns/${task.returnId}`,
                          `${pathname}${search}`,
                        )}
                        aria-label={`Review ${task.returnNumber}`}
                      >
                        {task.returnNumber}
                      </Link>
                      <p className="mt-1">
                        <Link
                          className="text-gray-800 hover:underline"
                          to={withRequestReturnTo(
                            `/orders/${task.orderId}`,
                            `${pathname}${search}`,
                          )}
                        >
                          {task.orderNumber || 'View order'}
                        </Link>
                      </p>
                      <p className="mt-2 text-gray-700">
                        {task.customerName || 'Customer unavailable'}
                      </p>
                      <p className="mt-1 text-xs text-gray-500">
                        {task.customerEmail}
                      </p>
                    </td>
                    <td className="max-w-xs px-4 py-4">
                      {task.items.map((item) => (
                        <div key={item.id} className="mb-2">
                          <p className="font-medium text-gray-800">
                            {item.title || 'Item unavailable'}
                          </p>
                          <p className="mt-1 text-xs text-gray-500">
                            {item.quantity} × {item.sku || 'No SKU'}
                          </p>
                        </div>
                      ))}
                    </td>
                    <td className="max-w-xs px-4 py-4">
                      {(Array.isArray(task.triggerReasons)
                        ? task.triggerReasons
                        : []
                      ).map((reason, index) => (
                        <p
                          key={index}
                          className="mb-2 text-gray-700"
                        >
                          {returnInspectionReasonLabel(reason)}
                        </p>
                      ))}
                      {task.warehouseNotes && (
                        <p className="mt-2 text-xs text-gray-500">
                          {task.warehouseNotes}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      <p className="text-gray-800">{date(task.deliveredAt)}</p>
                      <p className="mt-1 text-xs text-gray-500">
                        {task.carrier}
                      </p>
                      {task.trackingNumber &&
                        (safeTrackingUrl(task.trackingUrl) ? (
                          <a
                            className="mt-2 block text-xs text-blue-700 hover:underline"
                            href={safeTrackingUrl(task.trackingUrl)}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {task.trackingNumber}
                          </a>
                        ) : (
                          <p className="mt-2 text-xs text-gray-600">
                            {task.trackingNumber}
                          </p>
                        ))}
                    </td>
                    <td className="px-4 py-4">
                      <p className="font-medium text-gray-800">
                        {task.status === 'CLOSED'
                          ? 'Reviewed'
                          : task.status === 'AWAITING_WAREHOUSE'
                          ? 'Confirm warehouse receipt'
                          : 'Inspect returned items'}
                      </p>
                      <p className="mt-2 text-xs text-gray-500">
                        Opened {date(task.openedAt)}
                      </p>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!current.nodes.length && (
              <p className="px-5 py-12 text-center text-sm text-gray-500">
                No inspection tasks in this queue.
              </p>
            )}
          </div>
          <div className="flex items-center justify-between gap-3 border-t border-gray-200 bg-gray-50 px-4 py-3">
            <p className="text-xs text-gray-500">
              {current.totalCount} {current.totalCount === 1 ? 'task' : 'tasks'}
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                aria-label="Previous page"
                className={buttonClass}
                disabled={currentFilters.offset === 0}
                onClick={() =>
                  setFilters({
                    ...currentFilters,
                    offset: Math.max(0, currentFilters.offset - 25),
                  })
                }
              >
                Previous
              </button>
              <button
                type="button"
                aria-label="Next page"
                className={buttonClass}
                disabled={!current.hasMore || currentFilters.offset >= 10000}
                onClick={() =>
                  setFilters({
                    ...currentFilters,
                    offset: currentFilters.offset + 25,
                  })
                }
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
